/*    
<summary>
   This typescript file contains special datatype that enables for a variale to be set of predefined constant.
   This constant is used in unit data modules to provide default unit data.
   Developer: Aashish Singh, Created Date:29-March-2023
</summary>
*/

import moment from "moment";
// import CryptoJS from 'crypto-js';
import { IObservableInitialState, IOption } from "../models/ICommon";
import {
  IAddMedicalImages,
  IAddSaleItemVM,
  IAgreementRecordVM,
  IDiscountType,
  ISaleItemVM,
} from "../models/response/ISMRResponse";
import { IConsumptionTaxState } from "../models/state/IMasterSettingsState";
import dayjs from "dayjs";
import toast from "react-hot-toast";
import { formatMessage } from "../translations/format-message";
import html2pdf from "html2pdf.js";
import { serviceTypeEnum } from "./enums/service-type-enum";
import "react-quill/dist/quill.snow.css"; 
import base64 from "base-64";
import { date } from "yup";


export const emptyValue = "-";
export const emptyValueNA = "NA";
export const defautValueToBeChanged = -999;
export const superAdminUserName = "admin@test.com";
export const MAX_IMAGE_UPLOAD_LIMIT = 10;
// export const integerRegex = /^(?:\d+|)$/;
export const integerRegex = /^[\d０-９]*$/;
export const integerNumJpRegex = /^[\d０-９]*$/;
export const integerRegexWithNegativeNum = /^[-－]?[\d０-９]*$/;
export const stringRegex = /^[A-Za-z\s]*$/;
export const colorRegex = /^#(?!FFFFFF|ffffff)[0-9A-Fa-f]{6}$/;
export const stringEnJpRegex =
  /^[\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FAF\u3400-\u4DBF\u0041-\u005A\u0061-\u007A ]*$/;
export const stringJpRegexValidate =
  /^[\u30A0-\u30FF\uFF66-\uFF9F\u31F0-\u31FF]*$/; // katakana
export const stringJpRegex =
  /^[\u3040-\u309F\u30A0-\u30FF\uFF00-\uFFEF\uFF61-\uFF9F\uFF41-\uFF5A\uFF21-\uFF3A]*$/; // only hiragana and katakana
export const stringJpKanjiRegex =
  /^[\u3040-\u309F\u30A0-\u30FF-\u4E00-\u9FAF-\u3400-\u4DBF\uFF61-\uFF9F]*$/; // hiragana, katakana and kanji
export const floatRegex = /^(?:\d+(\.\d*)?|)$/;
export const alphaNumericCharcaterRegex = /^[a-zA-Z0-9]*$/;
export const DATE_FORMAT = "YYYY-MM-DD";
export const MONTH_YEAR_FORMAT = "YYYY-MM";
export const DATE_TIME_FORMAT = "YYYY-MM-DD HH:mm:ss";
export const DATE_TIME_FORMAT_FILENAME= "YYYY-MM-DD HH-mm-ss";
export const getDaysOptions = (noOfDays: number) => {
  let optionsArray = [];
  for (let i = 1; i <= noOfDays; i++) {
    optionsArray.push({ key: i, value: i });
  }
  return optionsArray;
};

export const clearLocalStorageFields = () => {
  const lang: string | null = localStorage.getItem("lizera_language");
  const pageLimitConfig: string | null = localStorage.getItem(
    "lizera_pageLimitConfig"
  );
  const tagPageLimitConfig: string | null = localStorage.getItem(
    "lizera_tagPageLimitConfig"
  );
  const selectedDays: string | null = localStorage.getItem(
    "lizera_selectedDays"
  );
  localStorage.clear();
  localStorage.setItem("lizera_language", lang === "en" ? "en" : "jp");
  if (pageLimitConfig)
    localStorage.setItem("lizera_pageLimitConfig", pageLimitConfig);
  if (tagPageLimitConfig)
    localStorage.setItem("lizera_tagPageLimitConfig", tagPageLimitConfig);
  if (selectedDays) localStorage.setItem("lizera_selectedDays", selectedDays);
  localStorage.setItem("lizera_language", lang === "en" ? "en" : "jp");
};

// Function to combine date and time into a single Date object
export const combineDateTime = (date: string, time: string): moment.Moment => {
  return moment(`${date} ${time}`, "YYYY-MM-DD HH:mm:ss");
};

export const isDateTimeOverlap = (
  startDate: string,
  startTime: string,
  endDate: string,
  endTime: string,
  existingData: any[],
  ssId: number
): boolean => {
  const newStart = combineDateTime(startDate, startTime);
  const newEnd = combineDateTime(endDate, endTime);

  for (const data of existingData) {
    const existingStart = combineDateTime(data.StartDate, data.StartTime);
    const existingEnd = combineDateTime(data.EndDate, data.EndTime);

    // Check if there is an overlap
    if (
      newStart.isBefore(existingEnd) &&
      newEnd.isAfter(existingStart) &&
      ssId != data.SsId
    ) {
      return true; // Overlap found
    }
  }

  return false; // No overlap
};

export const isSpecialDateTimeOverlap = (
  startDate: string,
  startTime: string,
  endDate: string,
  endTime: string,
  existingData: any[],
  ssId: number,
  scheduleType: number
): boolean => {
  const newStart = combineDateTime(startDate, startTime);
  const newEnd = combineDateTime(endDate, endTime);

  for (const data of existingData) {
    const existingStart = combineDateTime(data.StartDate, data.StartTime);
    const existingEnd = combineDateTime(data.EndDate, data.EndTime);

    // Check if there is an overlap
    if (ssId !== data.SsId) {
      if (data.ScheduleType == scheduleType) {
        if (
          (newStart.isBefore(existingEnd) && newEnd.isAfter(existingStart)) || // Overlaps with existing range
          (newStart.isBefore(existingStart) &&
            newStart.isSameOrAfter(existingStart)) || // New period starts before and overlaps with the existing start
          (newStart.isBefore(existingStart) && newEnd.isAfter(existingEnd)) // New period ends after existing period
        ) {
          if (ssId == data.SsId) {
            return false;
          } else {
            return true; // Overlap found
          }
        }
      }
    }
  }

  return false; // No overlap
};

export const convertToUTCStartDate = (dateString: any) => {
  const localDateTime = moment(dateString, "DD/MM/YYYY").startOf("day");
  const utcDateTime = localDateTime.utc();
  return utcDateTime.format(); // Format to ISO 8601 or use another format if needed
};

export const convertToUTCEndDate = (dateString: any) => {
  const localDateTime = moment(dateString, "DD/MM/YYYY").endOf("day");
  const utcDateTime = localDateTime.utc();
  return utcDateTime.format(); // Format to ISO 8601 or use another format if needed
};

// Validation function to check if StartDateTime is greater than EndDateTime
export const isStartDateTimeGreaterThanEndDateTime = (
  startDate: string,
  startTime: string,
  endDate: string,
  endTime: string
): boolean => {
  const start = combineDateTime(startDate, startTime);
  const end = combineDateTime(endDate, endTime);

  return start.isSameOrAfter(end);
};

export const isPresent = (
  scheduleArray: any,
  time: any,
  weekday: any,
  psId: number
) => {
  return scheduleArray.some((schedule: any) => {
    return (
      schedule.StartTime === time &&
      schedule.Weekday === weekday &&
      psId != schedule.PsId
    );
  });
};

export const getComponentName = (name: string) => {
  return name.replace(/^\//, "");
};

export const bytesToMegabytes = (bytes: number): number => {
  if (isNaN(bytes) || bytes < 0) {
    return 0;
  }
  return bytes / (1024 * 1024); // Convert bytes to MB and fix to 2 decimal places
};

// Format date time
export const formatDateTime = (dateTime: string | null) => {
  if (!dateTime) return "";
  try {
    const [time, isoDate] = dateTime.split("-");
    const date = new Date(isoDate);
    return `${date.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    })}, ${time}`;
  } catch (e) {
    return dateTime;
  }
};

export const isSlotBooked = (data: any, date: any, time: any) => {
  const formattedDate = moment(date).format("YYYY-MM-DD");
  return data.some(
    ({
      AppointmentDate,
      StartTime,
      EndTime,
    }: {
      AppointmentDate: any;
      StartTime: any;
      EndTime: any;
    }) => {
      const slotTime = moment(`${formattedDate} ${time}`, "YYYY-MM-DD HH:mm");
      const startTime = moment(
        `${formattedDate} ${StartTime}`,
        "YYYY-MM-DD HH:mm"
      );
      const endTime = moment(`${formattedDate} ${EndTime}`, "YYYY-MM-DD HH:mm");

      return (
        AppointmentDate === formattedDate &&
        (slotTime.isBetween(startTime, endTime, null, "[)") ||
          slotTime.isSame(startTime) ||
          slotTime.isSame(endTime))
      );
    }
  );
};

// Function to format the month and year for display
export const formatMonthYear = (date: Date) => {
  return date?.toLocaleString("default", { month: "long", year: "numeric" });
};

// Function to get the number of days in the current month
export const getDaysInMonth = (year: number, month: number) => {
  return new Date(year, month + 1, 0).getDate();
};

export const parseDateTime = (input: string) => {
  const [datePart, timePart] = input.split(" / ").map((part) => part.trim());
  const parsedDate = moment.parseZone(datePart);
  return {
    date: parsedDate.isValid()
      ? parsedDate.format(DATE_FORMAT)
      : "",
    time: timePart,
  };
};

type TimeRange = {
  StartTime: string;
  EndTime: string;
};

function adjustTimeRange(time: TimeRange): {
  StartTime: string;
  EndTime: string;
} {
  const toMinutes = (timeStr: string): number => {
    if (!timeStr || typeof timeStr !== "string") return 0;
    const [h, m] = timeStr.split(":").map((val) => parseInt(val, 10));
    if (isNaN(h) || isNaN(m)) return 0;
    return h * 60 + m;
  };

  const toHHMM = (totalMinutes: number): string => {
    const h = Math.floor(totalMinutes / 60)
      .toString()
      .padStart(2, "0");
    const m = (totalMinutes % 60).toString().padStart(2, "0");
    return `${h}:${m}`;
  };

  const startMin = toMinutes(time.StartTime);
  const endMin = toMinutes(time.EndTime);

  const flooredStart = Math.floor(startMin / 60) * 60;
  const ceiledEnd = Math.ceil(endMin / 60) * 60;

  return {
    StartTime: toHHMM(flooredStart),
    EndTime: toHHMM(ceiledEnd),
  };
}

const toMinutes = (timeStr: string) => {
  const [h, m] = timeStr.split(":").map(Number);
  return h * 60 + m;
};

export function getOverlappingEventsByTime(
  events: any[],
  { date, time, staffId }:any
) {
  const toMinutes = (timeStr: string) => {
    const [h, m] = timeStr.split(":").map(Number);
    return h * 60 + m;
  };

  const targetMin = toMinutes(time);
  const targetDate = moment(date).format("YYYY-MM-DD");

  return events?.filter((event: any) => {
    const eventDate = moment(event?.Date).format("YYYY-MM-DD");
    const start = toMinutes(event?.StartTime);
    const end = toMinutes(event?.EndTime);
    const sameStaff = Number(event?.StaffId) === Number(staffId);

    if (!sameStaff) return false;

    if (end > start) {
      if (eventDate !== targetDate) return false;
      return targetMin >= start && targetMin < end;
    }
    
    const nextDay = moment(eventDate).add(1, "day").format("YYYY-MM-DD");

    // If target is same day and time >= start (before midnight)
    if (targetDate === eventDate && targetMin >= start) return true;

    // If target is next day and time < end (after midnight)
    if (targetDate === nextDay && targetMin < end) return true;

    return false;
  });
}



export function getOverlappingEventsIntervalCheckByTime(
  allBreaks: any[],
  { date, startTime, endTime, staffId }: any
) {
  const toMinutes = (timeStr: string) => {
    const [h, m] = timeStr?.split(":")?.map(Number);
    return h * 60 + m;
  };

  const startMin = toMinutes(startTime);
  const endMin = toMinutes(endTime);

  return allBreaks?.filter((breakEvent) => {
    if (
      moment(breakEvent?.Date).format("YYYY-MM-DD") !== date ||
      Number(breakEvent?.StaffId) !== Number(staffId)
    )
      return false;

    const breakStart = toMinutes(breakEvent?.StartTime);
    const breakEnd = toMinutes(breakEvent?.EndTime);

    // Check if there is any overlap
    return startMin < breakEnd && breakStart < endMin;
  });
}

export function canCancelReservation(
  reservationDate: any,
  reservationTimeHHMM: any,
  cancelHoursBefore: any
) {
  // Combine date and time into a single datetime object
  const [hours, minutes] = reservationTimeHHMM.split(":").map(Number);
  const reservationDateTime = new Date(reservationDate);
  reservationDateTime.setHours(hours, minutes, 0, 0);

  // Calculate the deadline before which cancellation is allowed
  const cancellationDeadline = new Date(
    reservationDateTime.getTime() - cancelHoursBefore * 60 * 60 * 1000
  );
  const now = new Date();

  return now < cancellationDeadline;
}

export function isCurrentDateTimeGreater(dateStr: string, timeStr: string): boolean {
  if (!dateStr || !timeStr) return false;

  // Convert target date + time to a timestamp
  const [year, month, day] = dateStr.split("-").map(Number);
  const [hour, minute] = timeStr.split(":").map(Number);

  const targetTime = new Date(year, month - 1, day, hour, minute).getTime();
  const now = Date.now();

  return now > targetTime;
}

export function isCurrentDateGreater(dateStr: string): boolean {
  if (!dateStr) return false;

  // Parse the target date
  const [year, month, day] = dateStr.split("-").map(Number);
  const targetDate = new Date(year, month - 1, day);

  // Get today's date (set time to 00:00:00 for fair comparison)
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  targetDate.setHours(0, 0, 0, 0);

  return today.getTime() > targetDate.getTime();
}


export function formatMinutesToHours(mins: any) {
  const hours = Math.floor(mins / 60);
  const minutes = mins % 60;
  return `${hours} ${formatMessage("h")} ${minutes} ${formatMessage("min")}`;
}

export function getHoursDifferenceFromNow(
  dateStr: string,
  timeStr: string
): any {
  const inputDateTime = new Date(`${dateStr}T${timeStr}`);
  const now = new Date();

  // Explicitly get time in milliseconds to avoid TS errors
  const diffInMs = now.getTime() - inputDateTime.getTime();
  const diffInHours = diffInMs / (1000 * 60 * 60);
  let roundedDiffInHours = Math.abs(Math.round(diffInHours * 100) / 100);
  return roundedDiffInHours;
}

export function getOverlappingEventsByWeeklyTime(
  events: any,
  { date, time, staffId }: any
) {
  const toMinutes = (timeStr: any) => {
    const [h, m] = timeStr.split(":").map(Number);
    return h * 60 + m;
  };

  const targetMin = toMinutes(time);
  return events?.filter((event: any) => {
    if (moment(event?.Date).format("YYYY-MM-DD") !== date) return false;

    const start = toMinutes(event?.StartTime);
    const end = toMinutes(event?.EndTime);

    // Check if target time is within event range
    return targetMin >= start && targetMin < end;
  });
}

export function getOverlappingEventsByIdTime(
  events: any,
  { date, time, id }: any
) {
  const toMinutes = (timeStr: any) => {
    const [h, m] = timeStr.split(":").map(Number);
    return h * 60 + m;
  };

  const targetMin = toMinutes(time);
  return events?.filter((event: any) => {
    if (
      moment(event?.Date).format("YYYY-MM-DD") !== date ||
      Number(event?.Id) !== Number(id)
    )
      return false;

    const start = toMinutes(event?.StartTime);
    const end = toMinutes(event?.EndTime);

    // Check if target time is within event range
    return targetMin >= start && targetMin < end;
  });
}

export function getOverlappingBreaksByWeeklyTime(
  breaks: any,
  { date, time, staffId }: any
) {
  const toMinutes = (timeStr: any) => {
    const [h, m] = timeStr.split(":").map(Number);
    return h * 60 + m;
  };

  const targetMin = toMinutes(time);

  return (
    breaks
      ?.filter((event: any) => {
        if (moment(event?.Date).format("YYYY-MM-DD") !== date) return false;

        const start = toMinutes(event?.StartTime);
        const end = toMinutes(event?.EndTime);

        // ✅ Check if target time is within event range
        return targetMin >= start && targetMin < end;
      })
      // ✅ Sort by StartTime in ascending order
      .sort((a: any, b: any) => toMinutes(a.StartTime) - toMinutes(b.StartTime))
  );
}


export function getOverlappingBreaksIntervalCheckByTime(
  allBreaks: any[],
  { date, startTime, endTime, staffId }: any
) {
  const toMinutes = (timeStr: string) => {
    const [h, m] = timeStr?.split(":")?.map(Number);
    return h * 60 + m;
  };

  const startMin = toMinutes(startTime);
  const endMin = toMinutes(endTime);

  return allBreaks?.filter((breakEvent) => {
    if (
      moment(breakEvent?.Date).format("YYYY-MM-DD") !== date ||
      Number(breakEvent?.StaffId) !== Number(staffId)
    )
      return false;

    const breakStart = toMinutes(breakEvent?.StartTime);
    const breakEnd = toMinutes(breakEvent?.EndTime);

    // Check if there is any overlap
    return startMin < breakEnd && breakStart < endMin;
  });
}

export function getNextRoundedTime(interval: number): any {
  const now = dayjs();
  const minutes = now.minute();
  const remainder = minutes % interval;
  const diff = remainder === 0 ? 0 : interval - remainder;

  let time = now.add(diff, "minute").second(0).millisecond(0);
  return time.format("HH:mm");
}

export function getOverlappingBreaksByTime(breaks: any[], { date, time, staffId }: any) {
  const targetMin = toMinutes(time);
  const staffIdNum = Number(staffId);

  return breaks?.filter((brk: any) => {
    const { Date: brkDate, StaffId: brkStaffId, StartTime, EndTime } = brk;

    // Quick check for date and staff
    if (moment(brkDate).format("YYYY-MM-DD") !== date || Number(brkStaffId) !== staffIdNum) {
      return false;
    }

    const start = toMinutes(StartTime);
    const end = toMinutes(EndTime);

    // Check if target time is within break range
    return targetMin >= start && targetMin < end;
  });
}


export function getOverlappingBreaksByRange(
  breaks: any[],
  { date, startTime, endTime }: any
) {
  const toMinutes = (timeStr: string) => {
    const [h, m] = timeStr.split(":").map(Number);
    return h * 60 + m;
  };

  const rangeStart = toMinutes(startTime);
  const rangeEnd = toMinutes(endTime);

  return breaks?.filter((event) => {
    if (moment(event?.Date).format("YYYY-MM-DD") !== date) return false;

    const breakStart = toMinutes(event?.StartTime);
    const breakEnd = toMinutes(event?.EndTime);

    // Check if the time ranges overlap
    return (
      rangeStart < breakEnd && rangeEnd > breakStart // any overlap
    );
  });
}
export function truncateTimeToHour(timeStr: string) {
  const [hours] = timeStr.split(":");
  return `${hours.padStart(2, "0")}:00`;
}

export function getHoursFromReservationSetting(data: any) {
  let wday = moment(localStorage.getItem("currentDate")).day();
  let wdayObj = data?.BusinessSettings?.find((x: any) => x.WeekDays == wday);
  if (wdayObj) {
    return moment(wdayObj.OpenTime, "HH:mm").startOf("hour").format("HH:mm");
  } else {
    return "00:00";
  }
}

export function getOpenTimeFromReservationSetting(data: any) {
  if (data?.MinTime) {
    return moment(data?.MinTime, "HH:mm")?.format("HH:mm");
  } else {
    return "00:00";
  }
}

export function getOpenTimeFromReservationSettingForCustomerSide(data: any) {
  if (data?.MinTime) {
    return moment(data?.MinTime, "HH:mm")?.format("HH:mm");
  } else {
    return "00:00";
  }
}

export function getEventWithMostOverlaps(
  overlappedMap: { OverlappedEventsIds: number[] },
  allEvents: any[]
) {
  let arr: any[] = [];
  overlappedMap?.OverlappedEventsIds?.forEach((eventId: any) => {
    const event = allEvents?.find((event: any) => event.Id === eventId);
    if (event) {
      arr.push({
        Id: event.Id,
        Overlapped: event.OverlappedEventsIds?.length,
      });
    }
  });

  const maxOverlappedObj = arr.reduce(
    (max, curr) => (curr.Overlapped > (max?.Overlapped || 0) ? curr : max),
    null
  );
  if (maxOverlappedObj) {
    const event = allEvents?.find(
      (event: any) => event.Id === maxOverlappedObj?.Id
    );
    if (event) {
      return event;
    }
  } else {
    return null;
  }
}

export const generateWeeklyHours = (settings: any) => {
  if (!settings?.MinTime || !settings?.MaxTime) return [];

  const minHour = moment(settings.MinTime, "HH:mm").startOf("hour");
  const maxHour = moment(settings.MaxTime, "HH:mm").startOf("hour");
  const hoursDiff = maxHour.diff(minHour, "hours");

  if (hoursDiff < 0) return [];

  const result: string[] = [];
  for (let i = 0; i <= hoursDiff; i++) {
    result.push(minHour.clone().add(i, "hours").format("HH:mm"));
  }
  return result;
};

const holidayDateSet: Set<string> = new Set(
  [
    { date: "2025-01-01", name: "New Year's Day" },
    { date: "2025-01-12", name: "Coming of Age Day" },
    { date: "2025-02-11", name: "National Foundation Day" },
    { date: "2025-02-23", name: "Emperor's Birthday" },
    { date: "2025-03-20", name: "Vernal Equinox Day" },
    { date: "2025-04-29", name: "Shōwa Day" },
    { date: "2025-05-03", name: "Constitution Memorial Day" },
    { date: "2025-05-04", name: "Greenery Day" },
    { date: "2025-05-05", name: "Children's Day" },
    { date: "2025-05-06", name: "Substitute Holiday" },
    { date: "2025-07-20", name: "Marine Day" },
    { date: "2025-08-11", name: "Mountain Day" },
    { date: "2025-09-21", name: "Respect for the Aged Day" },
    { date: "2025-09-22", name: "Citizens Holiday" },
    { date: "2025-09-23", name: "Autumnal Equinox Day" },
    { date: "2025-10-12", name: "Sports Day" },
    { date: "2025-11-03", name: "Culture Day" },
    { date: "2025-11-23", name: "Labor Thanksgiving Day" },
  ].map((h) => moment(h.date).format("YYYY-MM-DD"))
);

export function isDayClosed(data: any): boolean {
  const currentDateStr = localStorage.getItem("currentDate");
  if (!currentDateStr) return false;

  const currentDate = moment(currentDateStr);
  const currentWDay = currentDate.day(); // 0 (Sun) to 6 (Sat)
  const formattedDate = currentDate.format("YYYY-MM-DD");

  const settingsArray = data?.BusinessSettings ?? [];

  // Convert to Map for O(1) lookup
  const settingsMap = new Map<number, any>();
  for (const setting of settingsArray) {
    settingsMap.set(setting.WeekDays, setting);
  }

  const isHoliday = holidayDateSet.has(formattedDate);
  if (isHoliday) {
    return settingsMap.get(7)?.IsClosed ?? true; // 7 for holiday default
  }

  return settingsMap.get(currentWDay)?.IsClosed ?? false;
}

/**
 * Determines whether the store is closed based on the provided data, current date,
 * and Japanese holidays.
 *
 * - Retrieves the current date from localStorage ("currentDate").
 * - Checks if the current date is a Japanese holiday.
 * - If it is a holiday and `data.IsHoliday` is true, checks the store's closed status for that day.
 * - If it is a holiday and `data.IsHoliday` is false, returns `true` (store is closed).
 * - If it is not a holiday, checks the store's closed status for the current weekday.
 *
 * @param data - Store data object, expected to contain at least an `IsHoliday` property and
 *               information required by `getStoreClosedStatusForDay`.
 * @returns `true` if the store is closed, `false` otherwise.
 */
export function IsStoreClosed(data: any): any {
  const currentDateStr = localStorage.getItem("currentDate");
  if (!currentDateStr) return false;

  const currentDate = moment(currentDateStr);
  const currentWDay = currentDate.day();
  const formattedDate = moment(currentDate).format("YYYY-MM-DD");
  let isNormalBusinessDay = getStoreClosedStatusForDay(data, currentWDay);

    let isItInHoliday = JapaneseHolidays?.some((holiday: any) => {
    return moment(holiday.date).format("MM-DD") === moment(formattedDate).format("MM-DD");
  });

  if (data?.IsHoliday == true) {
    if (isItInHoliday) {
      return getStoreClosedStatusForDay(data, 7); // <-- return added
    } else {
      return isNormalBusinessDay;
    }
  } else {
    return isNormalBusinessDay; // <-- return added
  }
}

export function IsStoreClosedByDate(data:any, date:any): any {
  const currentDateStr = date;
  if (!currentDateStr) return false;

  const currentDate = moment(currentDateStr);
  const currentWDay = currentDate.day();
  const formattedDate = moment(currentDate).format("YYYY-MM-DD");
  let isNormalBusinessDay = getStoreClosedStatusForDay(data, currentWDay);

  let isItInHoliday = JapaneseHolidays?.some((holiday: any) => {
  return moment(holiday.date).format("MM-DD") === moment(formattedDate).format("MM-DD");
});


  if (data?.IsHoliday == true) {
    if (isItInHoliday) {
      return getStoreClosedStatusForDay(data, 7); // <-- return added
    } else {
      return isNormalBusinessDay;
    }
  } else {
    return isNormalBusinessDay; // <-- return added
  }
}

export function IsItInHoliday(date: string): boolean {
  const formattedDate = moment(date).format("YYYY-MM-DD");

  let isItInHoliday = JapaneseHolidays?.some((holiday: any) => {
    return (
      moment(holiday.date).format("MM-DD") ===
      moment(formattedDate).format("MM-DD")
    );
  });
  return isItInHoliday;
}


export function IsStoreClosedByDateBookingCalendar(data:any, date:any): any {
  const currentDateStr = date;
  if (!currentDateStr) return false;
 
  const currentDate = moment(currentDateStr);
  const currentWDay = currentDate.day();
  const formattedDate = moment(currentDate).format("YYYY-MM-DD");
 
  let isItInHoliday = JapaneseHolidays?.some((holiday: any) => {
  return moment(holiday.date).format("MM-DD") === moment(formattedDate).format("MM-DD");
});

 
  if (isItInHoliday) {
    return true;
  } else {
    return false;
  }
}
export function IsStoreClosedByDateForShift(data:any, date:any): any {
  const currentDateStr = date;
  if (!currentDateStr) return false;

  const currentDate = moment(currentDateStr);
  const currentWDay = currentDate.day();
  const formattedDate = moment(currentDate).format("YYYY-MM-DD");
  let isNormalBusinessDay = getStoreClosedStatusForDay(data, currentWDay);

  let isItInHoliday = JapaneseHolidays?.some((holiday: any) => {
  return moment(holiday.date).format("MM-DD") === moment(formattedDate).format("MM-DD");
});


  if (data?.IsHoliday == true) {
    if (isItInHoliday) {
      return getStoreClosedStatusForDay(data, 7); // <-- return added
    } else {
      return isNormalBusinessDay;
    }
  } else {
    return isNormalBusinessDay; // <-- return added
  }
}

/**
 * Determines if the store is closed for a holiday based on the provided data and day.
 *
 * This function checks if the business settings indicate that the store is closed on holidays
 * (where `WeekDays == 7`). If the store is marked as closed for holidays, it delegates to
 * `getStoreClosedStatusForDay` to determine the closed status for the specific day.
 * Otherwise, it returns `false`.
 *
 * @param data - The business settings data object, expected to contain a `BusinessSettings` array.
 * @param day - The day to check the closed status for.
 * @returns `true` if the store is closed for the given day due to a holiday, otherwise `false`.
 */
export function getStoreClosedStatusForHoliday(data: any, day: any) {
  let getHolidayObject = data?.BusinessSettings?.find(
    (item: any) => item.WeekDays == 7
  );
  if (getHolidayObject?.IsClosed) {
    return getStoreClosedStatusForDay(data, day);
  } else {
    return false;
  }
}

export function getStoreClosedStatusForDay(data: any, day: any) {
  let dayObject = data?.BusinessSettings?.find(
    (item: any) => item.WeekDays == day
  );
  if (dayObject?.IsClosed) {
    return true;
  } else {
    return false;
  }
}

export function isDayClosedWeekly(data: any, date: any): boolean {
  if (!date) return false;
  const currentDate = moment(date);
  const currentWDay = currentDate.day(); // 0 (Sun) to 6 (Sat)
  const formattedDate = currentDate.format("YYYY-MM-DD");
  let isNormalBusinessDay = getStoreClosedStatusForDay(data, currentWDay);
  const isItInHoliday = JapaneseHolidays?.some((holiday: any) => {
  return moment(holiday.date).format("MM-DD") === moment(formattedDate).format("MM-DD");
});


  if (data?.IsHoliday) {
    if (isItInHoliday) {
      return getStoreClosedStatusForDay(data, 7);
    } else {
      return isNormalBusinessDay;
    }
  } else {
    return isNormalBusinessDay
  }
}

export function isTimeWithinShift(shift: any, timeLabel: string): boolean {
  if (!shift?.StartTime || !shift?.EndTime || !timeLabel) return false;

  const toMinutes = (timeStr: string) => {
    const [h, m] = timeStr.split(":").map(Number);
    return h * 60 + m;
  };

  const startMin = toMinutes(shift.StartTime);
  const endMin = toMinutes(shift.EndTime);
  const targetMin = toMinutes(timeLabel);

  // Overnight shift case
  if (endMin < startMin) {
    return targetMin >= startMin || targetMin < endMin;
  }

  // Regular case
  return targetMin >= startMin && targetMin < endMin;
}

export const isTimeOuterThanShift = (
  settings: any,
  timeLabel: string,
  date: string | Date
) => {
  let wday = moment(date).day();
  let wdayObj = settings?.BusinessSettings?.find(
    (x: any) => x.WeekDays == wday
  );
  const baseDate = moment(date).format("YYYY-MM-DD");
  if (wdayObj) {
    const openDateTime = moment(
      `${baseDate} ${wdayObj?.OpenTime}`,
      "YYYY-MM-DD HH:mm"
    );
    const closeDateTime = moment(
      `${baseDate} ${wdayObj?.ClosedTime}`,
      "YYYY-MM-DD HH:mm"
    );
    const targetDateTime = moment(
      `${baseDate} ${timeLabel}`,
      "YYYY-MM-DD HH:mm"
    );

    // Return true if it's outside the shift time
    return (
      targetDateTime.isBefore(openDateTime) ||
      targetDateTime.isSameOrAfter(closeDateTime)
    );
  } else {
    return false;
  }
};

/**
 * Calculates the minimum and maximum business hours for a given date based on business settings and Japanese holidays.
 *
 * @param settings - The business settings object, which should contain information about holidays and daily business hours.
 * @param date - The date (as a string or Date object) for which to determine the business hours.
 * @returns An object containing `MinTime` and `MaxTime` as strings in "HH:mm" format, representing the earliest and latest business hours for the specified date.
 *
 * @remarks
 * - If the date is a Japanese holiday and holiday settings are enabled, the function uses the holiday's business hours.
 * - If the business is closed on the specified day or holiday, the function returns the full day as available ("00:00" to "23:00").
 * - If the business is open, the function calculates the open and close times using `calculateOpenCloseTime`.
 * - The function relies on external variables/functions: `moment`, `JapaneseHolidays`, and `calculateOpenCloseTime`.
 */
export const getDailyBusinessHoursMinMaxTime = (
  settings: any,
  date: string | Date
) => {
  const baseDate = moment(date).format("YYYY-MM-DD");
  const day = moment(date).day();

  // Check if the date is a Japanese holiday
  const isHoliday = JapaneseHolidays?.some(
    (holiday: any) => moment(holiday.date).format("MM-DD") === moment(baseDate).format("MM-DD")
  );

  // Helper to get open/close for a specific day
  const getTimeForDay = (targetDay: number) => {
    const dayObj = settings?.BusinessSettings?.find(
      (x: any) => x.WeekDays === targetDay
    );

    if (!dayObj || dayObj.IsClosed) {
      return { MinTime: "00:00", MaxTime: "23:00" };
    }

    return calculateOpenCloseTime(baseDate, dayObj.OpenTime, dayObj.ClosedTime);
  };

  // Holiday logic
  if (isHoliday && settings?.IsHoliday) {
    const holidayObj = settings?.BusinessSettings?.find((x: any) => x.WeekDays === 7);
    if (holidayObj && !holidayObj.IsClosed) {
      return calculateOpenCloseTime(baseDate, holidayObj.OpenTime, holidayObj.ClosedTime);
    }
  }

  // Default weekday
  return getTimeForDay(day);
};


/**
 * Calculates the open and close time for a given date, adjusting the open time by subtracting 1 hour.
 * @param baseDate - The date in "YYYY-MM-DD" format
 * @param openTime - The opening time in "HH:mm" format
 * @param closedTime - The closing time in "HH:mm" format
 * @returns Object with MinTime and MaxTime as "HH:mm"
 */
// export const calculateOpenCloseTime = (
//   baseDate: string,
//   openTime: string,
//   closedTime: string
// ) => {
//   // Subtract 1 hour from open time for margin
//   const openMoment = moment(
//     `${baseDate} ${openTime}`,
//     "YYYY-MM-DD HH:mm"
//   ).subtract(1, "hour");
//   const closeMoment = moment(`${baseDate} ${closedTime}`, "YYYY-MM-DD HH:mm");

//   return {
//     MinTime: openMoment.format("HH:mm"),
//     MaxTime: closeMoment.format("HH:mm"),
//   };
// };

export const calculateOpenCloseTime = (
  baseDate: string,
  openTime: string,
  closedTime: string
) => {
  let openMoment = moment(`${baseDate} ${openTime}`, "YYYY-MM-DD HH:mm")
    .subtract(1, "hour");

  let closeMoment = moment(`${baseDate} ${closedTime}`, "YYYY-MM-DD HH:mm");
    // .add(1, "hour");

  const baseStart = moment(baseDate, "YYYY-MM-DD");               // midnight today
  const baseEnd = moment(baseDate, "YYYY-MM-DD").endOf("day");    // 23:59 today

  // Ensure MinTime doesn’t fall into previous day
  if (openMoment.isBefore(baseStart)) {
    openMoment = baseStart.clone();
  }

  // Ensure MaxTime doesn’t go into the next day
  if (closeMoment.isAfter(baseEnd)) {
    closeMoment = baseEnd.clone();
  }
  return {
    MinTime: openMoment.format("HH:mm"),
    MaxTime: closeMoment.format("HH:mm"),
  };
};


/**
 * Returns the minimum and maximum business hours for a given date, with a 1-hour margin before opening and after closing.
 * If the margin adjustment crosses into a different day, it falls back to the original open/close time.
 * @param settings - The settings object containing MinTime and MaxTime.
 * @param date - The date for which to calculate the business hours.
 * @returns An object with MinTime and MaxTime as "HH:mm" strings.
 */
export const getDailyBusinessHoursMinMaxTimeWeekly = (
  settings: any,
  date: string | Date
) => {
  const baseDate = moment(date).format("YYYY-MM-DD");

  const openMoment = moment(`${baseDate} ${settings?.MinTime}`, "YYYY-MM-DD HH:mm");
  const closeMoment = moment(`${baseDate} ${settings?.MaxTime}`, "YYYY-MM-DD HH:mm");

  // Calculate adjusted times
  let adjustedOpen = openMoment.clone().subtract(1, "hour");
  let adjustedClose = closeMoment.clone().add(1, "hour");

  //  Prevent open time from going to previous day
  if (adjustedOpen.isBefore(moment(baseDate, "YYYY-MM-DD"))) {
    adjustedOpen = openMoment.clone(); // keep original
  }

  //  Prevent close time from going to next day
  if (!adjustedClose.isSame(moment(baseDate, "YYYY-MM-DD"), "day")) {
    adjustedClose = closeMoment.clone(); // keep original
  }

  return {
    MinTime: adjustedOpen.format("HH:mm"),
    MaxTime: adjustedClose.format("HH:mm"),
  };
};



/**
 * Returns the minimum and maximum business hours for a given date, considering holidays and closed days.
 * If the business is closed on the given day or holiday, returns "00:00" to "23:00".
 * Otherwise, returns the open and close times for the day or holiday.
 * @param settings - The settings object containing BusinessSettings.
 * @param date - The date for which to calculate the business hours.
 * @returns An object with MinTime and MaxTime as "HH:mm" strings.
 */
export const getDailyBusinessHoursMinMaxTimeOrignal = (
  settings: any,
  date: string | Date
) => {
  const baseDate = moment(date).format("YYYY-MM-DD");
  const wday = moment(date).day();

  const businessSettings = settings?.BusinessSettings ?? [];
  const holiday = businessSettings.find((x: any) => x.WeekDays == 7);
  const weekday = businessSettings.find((x: any) => x.WeekDays == wday);

  const isHoliday = JapaneseHolidays?.some(
    (holiday: any) => moment(holiday.date).format("MM-DD") === moment(baseDate).format("MM-DD")
  );

  const formatRange = (obj: any) => ({
    MinTime: formatTime(baseDate, obj?.OpenTime),
    MaxTime: formatTime(baseDate, obj?.ClosedTime),
  });


  if (settings?.IsHoliday) {
    if (isHoliday) {
      if (holiday?.IsClosed) {
        return { MinTime: "00:00", MaxTime: "23:00" };
      } else {
        return formatRange(holiday);
      }
    }
    else{
      return formatRange(weekday);
    }
  }
  else{
    return formatRange(weekday);
  }
};


/**
 * Helper function to format a time string as "HH:mm" for a given date.
 * @param baseDate - The date in "YYYY-MM-DD" format.
 * @param time - The time string in "HH:mm" format.
 * @returns The formatted time string as "HH:mm".
 */
const formatTime = (baseDate: string, time: string) => {
  return moment(`${baseDate} ${time}`, "YYYY-MM-DD HH:mm").format("HH:mm");
};

export const isTimeOuterThanShiftCellColor = (
  settings: any,
  timeLabel: string,
  date: string | Date
) => {
  let wday = moment(date).day();
  let wdayObj = settings?.BusinessSettings?.find(
    (x: any) => x.WeekDays == wday
  );
  const baseDate = moment(date).format("YYYY-MM-DD");
  if (wdayObj) {
    let marginallyAdjustedOpenTime = moment(
      `${baseDate} ${wdayObj?.OpenTime}`,
      "YYYY-MM-DD HH:mm"
    ).subtract(1, "hour");
    let marginallyAdjustedCloseTime = moment(
      `${baseDate} ${wdayObj?.ClosedTime}`,
      "YYYY-MM-DD HH:mm"
    ).add(1, "hour");
    const openDateTime = moment(
      `${baseDate} ${marginallyAdjustedOpenTime}`,
      "YYYY-MM-DD HH:mm"
    );
    const closeDateTime = moment(
      `${baseDate} ${marginallyAdjustedCloseTime}`,
      "YYYY-MM-DD HH:mm"
    );
    const targetDateTime = moment(
      `${baseDate} ${timeLabel}`,
      "YYYY-MM-DD HH:mm"
    );

    // Return true if it's outside the shift time
    return (
      targetDateTime.isBefore(openDateTime) ||
      targetDateTime.isSameOrAfter(closeDateTime)
    );
  } else {
    return false;
  }
};

export const isTimeOuterThanShiftColourModeUsingMinMaxTimeEffort = (
  settings: { MinTime: string; MaxTime: string },
  timeLabel: string,
  date: string | Date
): boolean => {
  if (!settings?.MinTime || !settings?.MaxTime) return false;

  const toMinutes = (timeStr: string) => {
    const [h, m] = timeStr.split(":").map(Number);
    return h * 60 + m;
  };

  const targetMin = toMinutes(timeLabel);
  const openMin = toMinutes(settings.MinTime);
  const closeMin = toMinutes(settings.MaxTime);

  // Return true if outside business hours
  return targetMin < openMin || targetMin >= closeMin;
};

export const isTimeOuterThanShiftColourModeUsingMinMaxTimeEffortWeekly = (
  settings: any,
  timeLabel: string,
  date: string | Date
) => {
  const baseDate = moment(date).format("YYYY-MM-DD");
  const weekDay = moment(date).day();

  let timings = getDailyBusinessHoursMinMaxTimeOrignal(settings, date);

  const openMoment = moment(
    `${baseDate} ${timings.MinTime}`,
    "YYYY-MM-DD HH:mm"
  );
  const closeMoment = moment(
    `${baseDate} ${timings.MaxTime}`,
    "YYYY-MM-DD HH:mm"
  );
  const targetMoment = moment(`${baseDate} ${timeLabel}`, "YYYY-MM-DD HH:mm");

  if (
    !targetMoment.isValid() ||
    !openMoment.isValid() ||
    !closeMoment.isValid()
  ) {
    return false; // Bad data, treat as within bounds safely
  }

  // Check if outside the adjusted time window
  const isOutside =
    targetMoment.isBefore(openMoment) ||
    targetMoment.isSameOrAfter(closeMoment);
  return isOutside;
};

export function renderCount(data: any[], events: any[]) {
  let count = 0;
  data?.forEach((item: any) => {
    let finalObject = events
      .flatMap((item) => item.Appointments)
      .find((res) => res.Id == item?.Id);
    if (finalObject && finalObject?.IsRendered == true) {
      count++;
    }
  });
  return count;
}

export function formatTimeString(timeStr: any) {
  const [hour, minute] = timeStr.split(":").map(Number);
  const paddedHour = String(hour).padStart(2, "0");
  const paddedMinute = String(minute).padStart(2, "0");
  return `${paddedHour}:${paddedMinute}`;
}

export const initialStateValue: IObservableInitialState = {
  success: false,
  error: "",
  inProgress: false,
};

export const calculatePercentAmount = (amount: number, percentage: number) => {
  let tax = amount && percentage ? (amount * percentage) / 100 : 0;
  return Math.round(tax);
};

export const calculateDiscount = (item: ISaleItemVM) => {
  let totalDiscountAmount = 0;
  if (item.DiscountType == 0)
    totalDiscountAmount = calculatePercentAmount(
      item.Price,
      item.DiscountPercent
    );
  else if (item.DiscountType === 1) totalDiscountAmount = item.DiscountCash;
  else if (item.DiscountType === 2)
    totalDiscountAmount =
      item.DiscountCash +
      calculatePercentAmount(item.Price, item.DiscountPercent);
  return totalDiscountAmount;
};

export const calculateItemPriceAfterDiscount = (item: IAddSaleItemVM) => {
  const { Price, DiscountType, DiscountCash, DiscountPercent } = item;
  let discount = 0;
  if (DiscountType === 0) {
    discount = (Price * DiscountPercent) / 100;
  } else if (DiscountType === 1) {
    discount = DiscountCash;
  } else if (DiscountType === 2) {
    discount = DiscountCash;
    discount = DiscountCash + (Price * DiscountPercent) / 100;
  }
  discount = (discount);
  return Math.round(Price - discount);
};
export const calculateSummary = (
  mainStaff: { StaffId: number; IsNominationApplied: boolean },
  items: IAddSaleItemVM[],
  staffOptions: IOption[],
  consumptionTaxDetail: IConsumptionTaxState,
  overAllDiscountInfo: IDiscountType | null,
  isDataChanged: boolean = false,
  oldNominationFee?: number
): any => {
  let subtotal = 0;
  let taxTotal = 0;
  let nominationFee = 0;
  let overAllDiscount = 0;
  let nominationStaffSet = new Set<number>();
  let taxBreakdown: Record<string, number> = {};
  let consumptionTax = 0;
  let totalAmount = 0;

  if (isDataChanged) {
    let maintTmpStaffId =
      typeof mainStaff?.StaffId === "string"
        ? parseInt(mainStaff.StaffId)
        : mainStaff.StaffId;
    if (
      mainStaff?.IsNominationApplied &&
      !nominationStaffSet.has(maintTmpStaffId)
    ) {
      const staff = staffOptions.find((s) => s.id === maintTmpStaffId);
      if (staff && staff.data?.NominationFee && staff.data?.IsNominationFee) {
        nominationFee += staff.data.NominationFee;
        nominationStaffSet.add(maintTmpStaffId);
      }
    }
  }
  for (const item of items) {
    const {
      Price,
      Quantity,
      DiscountType,
      DiscountCash,
      DiscountPercent,
      Tax,
      StaffId,
      IsNominationApplied,
      ItemType,
      TaxAmount,
    } = item;

    // Calculate discount
    let discount = 0;
    if (DiscountType === 0) {
      discount = (Price * DiscountPercent) / 100;
    } else if (DiscountType === 1) {
      discount = DiscountCash;
    } else if (DiscountType === 2) {
      discount = DiscountCash;
      discount = DiscountCash + (Price * DiscountPercent) / 100;
    }
    const itemPriceINcludingTaxAfterDiscount = Math.round(Price - discount);
    const itemBasicPriceExcludingTaxAndTaxAmount = autoCalculateBasicPriceExcludingAndTaxAmount(
      itemPriceINcludingTaxAfterDiscount,
      Tax
    );
    const itemTax = Math.round(itemBasicPriceExcludingTaxAndTaxAmount.taxAmount) * Quantity;

    const itemPrice = Math.round(Price - discount);
    const itemTotal = itemPrice*Quantity;

    // Tax (based on discounted item total)
    subtotal += itemTotal;
    taxTotal += Math.round(itemTax);

    // Tax breakdown per percentage
    const taxKey = `${Tax}%`;
    taxBreakdown[taxKey] = (taxBreakdown[taxKey] || 0) + Math.round(itemTax);
    // Add nomination fee from staff data only once per staff
    if (isDataChanged) {
      let tempStaffId =
        typeof StaffId === "string" ? parseInt(StaffId) : StaffId;
      if (
        IsNominationApplied &&
        ItemType !== serviceTypeEnum.Product &&
        !nominationStaffSet.has(tempStaffId)
      ) {
        const staff = staffOptions.find((s) => s.id === tempStaffId);
        if (staff && staff.data?.NominationFee && staff.data?.IsNominationFee) {
          nominationFee += staff.data.NominationFee;
          nominationStaffSet.add(tempStaffId);
        }
      }
    } else nominationFee = oldNominationFee ? oldNominationFee : 0; // if data is not changed then use the old nomination fee
  }

  let consumtionTaxPercent =
    typeof consumptionTaxDetail.TaxValue === "string"
      ? parseInt(consumptionTaxDetail.TaxValue)
      : consumptionTaxDetail.TaxValue;
  consumptionTax = (nominationFee * consumtionTaxPercent) / 100;
  const consumptionTaxKey = `${consumtionTaxPercent}%`;
  if (taxBreakdown[consumptionTaxKey] !== undefined) {
    taxBreakdown[consumptionTaxKey] += Math.round(consumptionTax);
  }
  //subtotal = subtotal + nominationFee + consumptionTax;
  totalAmount = subtotal;

  // if (overAllDiscountInfo !== null) {
  //   if (overAllDiscountInfo.DiscountType === 0) {
  //     overAllDiscount =
  //       (totalAmount * overAllDiscountInfo.DiscountPercent) / 100;
  //   } else if (overAllDiscountInfo.DiscountType === 1) {
  //     overAllDiscount = overAllDiscountInfo.DiscountCash;
  //   } else if (overAllDiscountInfo.DiscountType === 2) {
  //     overAllDiscount =
  //       overAllDiscountInfo.DiscountCash +
  //       (totalAmount * overAllDiscountInfo.DiscountPercent) / 100;
  //   }
  // }
  totalAmount = totalAmount - overAllDiscount;
  totalAmount = totalAmount + nominationFee + consumptionTax;
  return {
    Subtotal: Math.round(subtotal),
    NominationFee: Math.round(nominationFee),
    TaxTotal: Math.round(taxTotal),
    OverAllDiscount: Math.round(overAllDiscount),
    TaxBreakdown: taxBreakdown,
    ConsumptionTax: Math.round(consumptionTax),
    TotalAmount: Math.round(totalAmount),
  };
};

export const imageThumbnailCompressOptions = {
  maxSizeMB: 0.1,
  maxWidthOrHeight: 200,
  useWebWorker: true,
  fileType: "image/webp",
};

export const imageCompressOptions = {
  maxSizeMB: 0.5,
  maxWidthOrHeight: 1600,
  useWebWorker: true,
  fileType: "image/webp",
};

export const getSortedIdsByStartTime = (
  allData: any[],
  selectedIds: number[]
): number[] => {
  return allData
    .filter((item) => selectedIds.includes(item.Id))
    .sort((a, b) =>
      moment(a.StartTime, "HH:mm").diff(moment(b.StartTime, "HH:mm"))
    )
    .map((item) => item.Id);
};

export const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
    reader.readAsDataURL(file);
  });
};

export const resizeImage = (
  file: File,
  maxWidth: number,
  maxHeight: number,
  quality: number
): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();

    reader.onload = (e) => {
      img.src = e.target?.result as string;
    };

    img.onload = () => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      const scale = Math.min(maxWidth / img.width, maxHeight / img.height);
      canvas.width = img.width * scale;
      canvas.height = img.height * scale;

      ctx?.drawImage(img, 0, 0, canvas.width, canvas.height);
      const webpBase64 = canvas.toDataURL("image/webp", quality);

      resolve(webpBase64);
    };

    img.onerror = reject;
    reader.onerror = reject;

    reader.readAsDataURL(file);
  });
};

export const compressBase64ImageToThumbnail = (
  base64: string,
  maxWidth: number,
  maxHeight: number
): Promise<string> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.src = base64;

    img.onload = () => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      const scale = Math.min(maxWidth / img.width, maxHeight / img.height);
      canvas.width = img.width * scale;
      canvas.height = img.height * scale;

      ctx?.drawImage(img, 0, 0, canvas.width, canvas.height);
      const webpBase64 = canvas.toDataURL("image/webp", 0.1); // webp base64 string

      resolve(webpBase64);
    };

    img.onerror = reject;
  });

export const convertFileToWebP = async (file: Blob): Promise<Blob> => {
  const bitmap = await createImageBitmap(file);

  const canvas = document.createElement("canvas");
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;

  const ctx = canvas.getContext("2d");
  ctx?.drawImage(bitmap, 0, 0);

  return new Promise((resolve) => {
    canvas.toBlob(
      (blob) => resolve(blob as Blob),
      "image/webp",
      0.9 // 👈 keep good quality
    );
  });
};

export const calculateTreatmentTimings = (
  services: any[],
  startTime: string,
  postBreakTime: number
) => {
  const result: any[] = [];
  let currentTime = startTime;

  const addMinutes = (time: string, minutesToAdd: number) => {
    const [hours, minutes] = time.split(":").map(Number);
    let totalMinutes = hours * 60 + minutes + minutesToAdd;

    // Cap the time at 23:59 (1439 minutes)
    if (totalMinutes >= 24 * 60) {
      totalMinutes = 24 * 60 - 1;
    }

    const newHours = Math.floor(totalMinutes / 60)
      .toString()
      .padStart(2, "0");
    const newMinutes = (totalMinutes % 60).toString().padStart(2, "0");
    return `${newHours}:${newMinutes}`;
  };

  for (let service of services) {
    const endTime = addMinutes(currentTime, service.TimeValue);

    // If current time already hit the limit, stop processing further
    if (currentTime === "23:59") break;

    result.push({
      ...service,
      StartTime: currentTime,
      EndTime: endTime,
    });

    // If we’ve reached 23:59, stop adding more
    if (endTime === "23:59") {
      currentTime = "23:59";
      break;
    }

    currentTime = endTime;
  }

  // Add interval only if still before 23:59
  if (currentTime !== "23:59") {
    const intervalEndTime = addMinutes(currentTime, postBreakTime);
    result.push({
      ServiceRequestID: null,
      ServiceId: null,
      ServiceType: null,
      ServiceName: formatMessage("interval"),
      TotalTime: postBreakTime,
      StartTime: currentTime,
      EndTime: intervalEndTime,
    });
  }

  return result;
};


export const checkIsSeatsAvailable = (
  noOfSeatAvailable: number,
  events: any[],
  startDateTime: string,
  totalDuration: number
): boolean => {
  const endTime = moment(startDateTime).add(totalDuration, "minutes");
  const overlappingEvents = events.filter((event) => {
    const baseDate = moment(event.Date).format("YYYY-MM-DD");
    const eventStart = moment(
      `${baseDate} ${event.StartTime}`,
      "YYYY-MM-DD HH:mm:ss"
    );
    const eventEnd = moment(
      `${baseDate} ${event.EndTime}`,
      "YYYY-MM-DD HH:mm:ss"
    );
    return (
      eventStart.isBefore(endTime) && eventEnd.isSameOrAfter(startDateTime)
    );
  });
  const seatAvailable = noOfSeatAvailable || 0;
  return overlappingEvents.length < seatAvailable;
};

export const inValidCharacterForStoreName = [
  "?",
  ">",
  "<",
  "'",
  '"',
  ":",
  ";",
  ",",
  "+",
  "-",
  "(",
  ")",
  "*",
  "&",
  "^",
  "%",
  "$",
  "#",
  "{",
  "[",
  "]",
  "}",
  "|",
  "/",
  "\\",
  "=",
  "_",
  "!",
  "~",
  "`",
  ".",
  "@",
  "0",
  "1",
  "2",
  "3",
  "4",
  "5",
  "6",
  "7",
  "8",
  "9",
];

export const getOptionNameFromServiceName = (serviceName: string): string => {
  const parts = serviceName.split("_");
  return parts.length > 1 ? parts.slice(1).join("_") : "";
};

export const autoCalculateBasicAndTaxAmount = (
  amountIncludingTax: number,
  taxAmount: number
): { basicAmount: number; taxAmount: number } => {
  // let basicAmount = 0;
  // let taxAmount = 0;
  // basicAmount = amountIncludingTax / (1 + taxPercent / 100);
  // taxAmount = amountIncludingTax - basicAmount;
  return {
    basicAmount: amountIncludingTax - taxAmount,
    taxAmount: taxAmount,
  };
};

export const autoCalculateBasicPriceExcludingAndTaxAmount = (
  amountIncludingTax: number,
  taxPercent: number
): { basicAmount: number; taxAmount: number } => {
  let basicAmount = 0;
  let taxAmount = 0;
  basicAmount = amountIncludingTax / (1 + taxPercent / 100);
  taxAmount = amountIncludingTax - Math.round(basicAmount);
  return {
    basicAmount: amountIncludingTax - taxAmount,
    taxAmount: taxAmount,
  };
};

export const generatePDF = (salesAgreementRecordDetail: IAgreementRecordVM) => {
  const { Agreement, Image, AgreementImages } = salesAgreementRecordDetail;
  if (AgreementImages && AgreementImages.length === 0 && !Image) return;

  const container = document.createElement("div");
  // Add Quill styles manually inside the container
  const quillStyle = document.createElement("style");
  quillStyle.innerHTML = `
    ${require("react-quill/dist/quill.snow.css").toString()}
    body { font-family: Arial, sans-serif; font-size: 14px; }
    h2 { margin: 10px 0; text-align: center; }
    img { max-width: 100%; height: auto; }
  `;
  container.appendChild(quillStyle);

  if (Image !== null) {
    container.innerHTML += `
      <div style="font-family: Arial, sans-serif; font-size: 14px; padding: 20px;">
        <div style="page-break-inside: avoid; break-inside: avoid; margin-top: 40px;">  
          <h2>${formatMessage("agreement")}</h2>
          <div class="ql-editor" style="margin-bottom: 0px;">${Agreement}</div>
        </div>
        <div style="page-break-inside: avoid; break-inside: avoid; margin-top: 40px;">
          <h2>${formatMessage("signature")}</h2>
          <img src="${Image}" style="max-width: 300px; height: auto;" />
        </div>
      </div>
    `;
  } else {
    let imageHtml = "";
    if (AgreementImages && AgreementImages.length > 0) {
      imageHtml = AgreementImages.map((img: IAddMedicalImages) => {
        return `<img src="${img.ImageBase64}" style="max-width: 100%; height: auto; margin: 0px; padding: 0px;" />`;
      }).join("");
    }
    container.innerHTML += `
        <div style="page-break-inside: avoid; break-inside: avoid; margin: 0px; padding: 0px;">
          ${imageHtml}
        </div>
    `;
  }

  const opt = {
    margin: 10,
    filename: `${moment(new Date()).format(DATE_TIME_FORMAT)}-sales-agreement.pdf`,
    image: { type: "jpeg", quality: 0.98 },
    html2canvas: { scale: 2 },
    jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
    pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }, // <– Key fix
  };

  html2pdf()
    .from(container)
    .set(opt)
    .save()
    .then(() => {
      toast.success(formatMessage("downloaded_started_successfully"));
    });
};

export const JapaneseHolidays = [
  { date: "2025-01-01", name: "New Year's Day" },
  { date: "2025-01-12", name: "Coming of Age Day" },
  { date: "2025-02-11", name: "National Foundation Day" },
  { date: "2025-02-23", name: "Emperor's Birthday" },
  { date: "2025-03-20", name: "Vernal Equinox Day" },
  { date: "2025-04-29", name: "Shōwa Day" },
  { date: "2025-05-03", name: "Constitution Memorial Day" },
  { date: "2025-05-04", name: "Greenery Day" },
  { date: "2025-05-05", name: "Children's Day" },
  { date: "2025-05-06", name: "Substitute Holiday" },
  { date: "2025-07-20", name: "Marine Day" },
  { date: "2025-08-11", name: "Mountain Day" },
  { date: "2025-09-21", name: "Respect for the Aged Day" },
  { date: "2025-09-22", name: "Citizens Holiday" },
  { date: "2025-09-23", name: "Autumnal Equinox Day" },
  { date: "2025-10-12", name: "Sports Day" },
  { date: "2025-11-03", name: "Culture Day" },
  { date: "2025-11-23", name: "Labor Thanksgiving Day" }
];

export function isAnyTreatmentInvalidByDate(
  selectedServices: any[],
  selectedDate: string,
  getAccordionDataById: (id: number) => any
): boolean {
  if (!Array.isArray(selectedServices) || !selectedDate) {
    return true;
  }

  // Clean up selected date
  const cleanDateStr = selectedDate.split(" /")[0].trim();
  const selectedMoment = moment(new Date(cleanDateStr)).startOf("day");

  if (!selectedMoment.isValid()) {
    return true;
  }

  for (const item of selectedServices) {
    const treatmentData = getAccordionDataById(item.TreatmentId);
    if (!treatmentData) continue; // Skip invalid treatments

    const start = moment(treatmentData.StartDate).startOf("day");
    const end = moment(treatmentData.EndDate).startOf("day");

    // If start or end date is invalid, skip this treatment
    if (!start.isValid() || !end.isValid()) {
      continue;
    }

    // If selected date is NOT in range, return true
    if (!selectedMoment.isBetween(start, end, "day", "[]")) {
      return true;
    }
  }

  // If all valid treatments pass
  return false;
}

export function isAnyTreatmentInvalidByStaff(
  selectedServices: any[],
  selectedStaff: number,
  getAccordionDataById: (id: number) => any
): any {
  if (!Array.isArray(selectedServices)) {
    return false;
  }

  for (const item of selectedServices) {
    const treatmentData = getAccordionDataById(item.TreatmentId);
    if (!treatmentData) continue; // Skip if data is missing

    const staffMappings = treatmentData.treatmentStaffMappingVMs;
    let isCheckOnlyGender = selectedStaff > 0 ? false : true;
    if(isCheckOnlyGender){
      let isMappingSuccess = staffMappings?.some((mapping: any) => {
        if(selectedStaff == -2){
          return Number(mapping?.Gender == 1);
        }
        else if(selectedStaff == -1){
          return Number(mapping?.Gender == 0);
        }
        else{
          return true;
        }
      });
      if(isMappingSuccess == false){
        return false;
      }
      else{
        return true;
      }
    }
    else{
      return staffMappings?.some((mapping: any) => {
        return Number(mapping?.StaffId) === Number(selectedStaff);
      });
    }
  }
}

export const formatNumberWithCommas = (num: number) => {
    return new Intl.NumberFormat('en-US', {
      maximumFractionDigits: 2 // No decimal places
  })?.format(num);
}

export function isTimeSlotWithinWindow(
  hours: number,
  slotStr: string,
  slotDate: string
): boolean {
  const format = "HH:mm";

  const start = moment(); // now
  const end = moment().add(hours, "hours"); // now + hours

  // Parse slot time for today
  const slot = moment(slotStr, format).set({
    year: moment(slotDate).year(),
    month: moment(slotDate).month(),
    date: moment(slotDate).date(),
  });

  return slot.isSameOrAfter(start) && slot.isSameOrBefore(end);
}


export const safeBase64Encode = (str: string): string => {
  const utf8Bytes = new TextEncoder().encode(str);
  // Convert Uint8Array → binary string
  const binary = Array.from(utf8Bytes, (b) => String.fromCharCode(b)).join("");
  return base64.encode(binary);
};